import { SwitchOptions } from '../../../../../interfaces/src/lib/types/index';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface IFlyAndCruiseAEMConfig {
  flightToggleLabel?: string;
  flightToggleAriaLabel?: string;
  flightToggleIcon?: string;
  flightToggleIconAriaLabel?: string;
  flightFromPriceLabel?: string;
  flightFromPriceAddedLabel?: string;
  singleFlightFromLabel?: string;
  flightPricePlaceholder?: string;
  transitionDelay?: number;
  backupNaNLabel?: string;
  flightToggleNotEnabledAriaLabel?: string;
  flightToggleEnabledAriaLabel?: string;
}

export interface INumberOfNightsAEMConfig {
  numberOfNightsLabel?: string;
  numberOfSingleNightLabel?: string;
}

export interface IAirportData {
  isFlightPriceIncluded: boolean;
  lowestFitFlightPrice?: number;
}

export type DateslayerState = {
  selectedCabinCruiseId?: number;
  selectedCabinTypeBackup: string;
  toolTipInfo: {
    includedTooltipTitle?: string;
    includedTooltipDescription?: string;
    servicesFeeText?: string;
    flightWithNoTransferText?: string;
    flightWithTransferText?: string;
    harbourFees?: string;
    harbourFeesValue?: string;
    harbourFeesIncludedLabel?: string;
    hscFees?: string;
    hscFeesValue?: string;
    portCharges?: string;
    includedPortChargesLabel?: string;
  };
  mandatoryInsuranceNoteTooltip?: string;
  isFlightToggleEnabled: boolean;
  lastClickedCruiseItinerary?: string;

  activeToggleOption: SwitchOptions;
  flyAndCruiseAEMConfig: IFlyAndCruiseAEMConfig;
  numberOfNightsAEMConfig: INumberOfNightsAEMConfig;
  airportData: IAirportData;
  selectedDateUrl: string;
  ageRestrictions?: {
    allowedInfant?: boolean;
    minAgePax?: number | null;
  };
  transferInfo?: {
    hasBus?: boolean;
    hasTrains?: boolean;
  };
  allCardExists?: boolean;
  isCruiseFlightEnabled?: boolean;
  hasAtLeastOneAirAvailable?: boolean;
};

const initialState: DateslayerState = {
  selectedCabinCruiseId: undefined,
  selectedCabinTypeBackup: '',
  toolTipInfo: {
    includedTooltipTitle: '',
    includedTooltipDescription: '',
    servicesFeeText: '',
    flightWithNoTransferText: '',
    flightWithTransferText: '',
    harbourFees: '',
    harbourFeesValue: '',
    harbourFeesIncludedLabel: '',
    hscFees: '',
    hscFeesValue: '',
    portCharges: '',
  },
  isFlightToggleEnabled: false,
  mandatoryInsuranceNoteTooltip: '',
  lastClickedCruiseItinerary: '',
  activeToggleOption: SwitchOptions.OPTION1,
  flyAndCruiseAEMConfig: {
    flightToggleLabel: '',
    flightToggleAriaLabel: '',
    flightToggleIcon: '',
    flightToggleIconAriaLabel: '',
    flightFromPriceLabel: '',
    flightFromPriceAddedLabel: '',
    singleFlightFromLabel: '',
    flightPricePlaceholder: '',
    transitionDelay: 0.5,
    backupNaNLabel: '',
  },
  numberOfNightsAEMConfig: {
    numberOfNightsLabel: '',
    numberOfSingleNightLabel: '',
  },
  airportData: {
    isFlightPriceIncluded: false,
    lowestFitFlightPrice: 0,
  },
  selectedDateUrl: '',
  ageRestrictions: {
    allowedInfant: true,
    minAgePax: null,
  },
  isCruiseFlightEnabled: false,
};

const dateslayerSlice = createSlice({
  name: 'dateslayer',
  initialState,
  reducers: {
    setSelectedCabinCruiseId: (
      state: DateslayerState,
      action: PayloadAction<number | undefined>
    ) => {
      state.selectedCabinCruiseId = action.payload;

      return state;
    },
    setSelectedCabinTypeBackup: (state: DateslayerState, action: PayloadAction<string>) => {
      state.selectedCabinTypeBackup = action.payload;
      return state;
    },
    setToolTipInfo: (
      state: DateslayerState,
      action: PayloadAction<{
        includedTooltipTitle?: string;
        includedTooltipDescription?: string;
        servicesFeeText?: string;
        flightWithNoTransferText?: string;
        flightWithTransferText?: string;
        harbourFees?: string;
        harbourFeesValue?: string;
        harbourFeesIncludedLabel?: string;
        hscFees?: string;
        hscFeesValue?: string;
        portCharges?: string;
      }>
    ) => {
      state.toolTipInfo = action.payload;
    },
    setIsFlightToggleEnabled: (state: DateslayerState, action: PayloadAction<boolean>) => {
      state.isFlightToggleEnabled = action.payload;
    },
    setMandatoryInsuranceNoteTooltip: (state: DateslayerState, action: PayloadAction<string>) => {
      state.mandatoryInsuranceNoteTooltip = action.payload;
    },
    setLastClickedCruiseItinerary: (state: DateslayerState, action: PayloadAction<string>) => {
      state.lastClickedCruiseItinerary = action.payload;
    },
    setActiveToggleOption: (state: DateslayerState, action: PayloadAction<SwitchOptions>) => {
      state.activeToggleOption = action.payload;
    },
    setFlyAndCruiseAEMConfig: (
      state: DateslayerState,
      action: PayloadAction<IFlyAndCruiseAEMConfig>
    ) => {
      state.flyAndCruiseAEMConfig = action.payload;
    },
    setNumberOfNightsAEMConfig: (
      state: DateslayerState,
      action: PayloadAction<INumberOfNightsAEMConfig>
    ) => {
      state.numberOfNightsAEMConfig = action.payload;
    },
    setisFlightPriceIncluded: (state: DateslayerState, action: PayloadAction<boolean>) => {
      state.airportData.isFlightPriceIncluded = action.payload;
    },
    setLowestFitFlightPrice: (state: DateslayerState, action: PayloadAction<number>) => {
      state.airportData.lowestFitFlightPrice = action.payload;
    },
    setSelectedDateUrl: (state: DateslayerState, action: PayloadAction<string>) => {
      state.selectedDateUrl = action.payload;
    },
    setAgeRestrictions: (
      state: DateslayerState,
      action: PayloadAction<{ allowedInfant?: boolean; minAgePax?: number | null }>
    ) => {
      state.ageRestrictions = action.payload;
    },
    setTransferInfo: (
      state: DateslayerState,
      action: PayloadAction<{ hasBus: boolean; hasTrains: boolean }>
    ) => {
      state.transferInfo = action.payload;
    },
    isCruiseFlightEnabled: (state: DateslayerState, action: PayloadAction<boolean>) => {
      state.isCruiseFlightEnabled = action.payload;
    },
  },
});

export const {
  setSelectedCabinCruiseId,
  setSelectedCabinTypeBackup,
  setIsFlightToggleEnabled,
  setToolTipInfo,
  setMandatoryInsuranceNoteTooltip,
  setLastClickedCruiseItinerary,
  setActiveToggleOption,
  setFlyAndCruiseAEMConfig,
  setNumberOfNightsAEMConfig,
  setisFlightPriceIncluded,
  setLowestFitFlightPrice,
  setSelectedDateUrl,
  setAgeRestrictions,
  setTransferInfo,
  isCruiseFlightEnabled,
} = dateslayerSlice.actions;
export const dateslayerReducer = dateslayerSlice.reducer;
